import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ImpersonationContext, type ApiClient } from '@center-technologies/shared-api-clients';
import type { ApiClients } from '../context/ApiClientsContext';
import { mergeSessionPatch } from '../context/ViewStateContext';
import { composeJsonPatch, patchFoldsAcrossDelete } from '../utils/deepMergeJson';
import {
  getContextViewState,
  putContextViewState,
  deleteContextViewState,
  type ContextViewStateOverlay,
} from '../api/contextApi';
import type { BaseViewConfig, BaseViewConfigPatch, ViewConfig } from 'types/contextView';

/**
 * Product constant — see specs/view-personalization-overlay.md § Dynamic
 * configs for why this is not a Statsig dynamic config (no reader exists on
 * the platform).
 *
 * A trailing-edge debounce with no max-wait: a continuous stream of patches
 * closer together than this never flushes until the user pauses. That is the
 * intended behaviour for the interactions that produce them (column drag,
 * rapid collapse/expand) — the write is worth issuing once the arrangement
 * settles, not mid-gesture — and every path that could lose the buffer
 * (unmount, board switch, identity switch) flushes explicitly.
 */
export const AUTOSAVE_DEBOUNCE_MS = 800;

/** The caller's own personalization overlay, shaped to drop straight into `resolveViewConfig`'s `user` layer. */
export interface ViewPersonalizationOverlay {
  base: Partial<BaseViewConfig>;
  view: Partial<ViewConfig>;
}

export interface UseViewPersonalizationOptions {
  /**
   * Fired when a write (debounced PUT or an explicit `reset()` DELETE) fails.
   * Non-blocking by design — the hook never throws and never blanks the board
   * over a write failure. Callers surface a toast/notice from this callback;
   * design-system stays decoupled from the toast/analytics vendor the same
   * way `ContextViewEditorOverlayOrganism`'s `onAnalyticsEvent` does.
   */
  onWriteError?: (error: unknown) => void;
}

export interface UseViewPersonalizationReturn {
  /** The resolved overlay — empty (`{ base: {}, view: {} }`) when none exists. */
  overlay: ViewPersonalizationOverlay;
  /** True once the overlay carries at least one field. Drives the Card A "Reset my adjustments" affordance. */
  hasOverlay: boolean;
  /** Debounced write into the overlay's base-config half (e.g. `levelOverrides`). Admits `null` (a `BaseViewConfigPatch`) to delete a key or a per-entry `levelFilters` value rather than replace it. */
  patchBase: (patch: BaseViewConfigPatch) => void;
  /** Debounced write into the overlay's view-config half (e.g. `columnState`, `collapsedNodeIds`, `defaultCardView`). */
  patchView: (patch: Partial<ViewConfig>) => void;
  /** Deletes the overlay row, cancels any pending debounced write, and clears local + cached state. */
  reset: () => Promise<void>;
  /**
   * False until the overlay read has actually resolved (or there was nothing to
   * read). `overlay` starts at `EMPTY_OVERLAY` and is only filled by an async
   * GET, so "no overlay" and "not loaded yet" are otherwise indistinguishable —
   * and every consumer that samples the overlay ONCE, at a moment near mount,
   * reads the empty placeholder and concludes the user has no personalization.
   * `TreeMapOrganism`'s `hadSuppressedUserLayer` metric was doing exactly that
   * (EAINT-793): a cold page load from a pasted link — the only way that
   * feature is ever entered — always sampled before the GET returned, so the
   * metric reported "suppression did not matter" for every recipient, which is
   * the opposite of what it was added to measure.
   */
  settled: boolean;
}

const EMPTY_OVERLAY: ViewPersonalizationOverlay = { base: {}, view: {} };
const DEFAULT_VIEW_KEY = '__default__';
/** Cache-key segment for "acting as myself" — i.e. no impersonation session in play. */
const SELF_IDENTITY_KEY = 'self';

/**
 * The cache key carries the ACTING IDENTITY, not just the board.
 *
 * The `schemaClient` reference cannot stand in for identity: `ApiClientsProvider`
 * deliberately does NOT rebuild its clients when impersonation starts (see
 * `ApiClientsContext.tsx` — the bearer token is resolved per-request through
 * `tokenProvider`, so the client instances are latched and stable). Without the
 * identity segment, an admin who opens a board and then impersonates someone
 * gets their OWN overlay — collapsed node ids and all — seeded synchronously
 * onto the impersonated user's board until the GET resolves. That is exactly the
 * cross-identity leak SC5 forbids.
 */
function buildOverlayCacheKey(
  contextRef: string,
  viewSystemName: string | null | undefined,
  actingIdentityKey: string
): string {
  return `${actingIdentityKey}::${contextRef}::${viewSystemName ?? DEFAULT_VIEW_KEY}`;
}

interface CachedOverlayEntry {
  /**
   * The `schemaClient` this entry was resolved with, compared by REFERENCE —
   * not serialised into the key. `String(client)` collapses to
   * `'[object Object]'` for every instance, so a serialised key would let a
   * different identity adopt a stale cached answer (same reasoning as
   * `useContextViewPermissions`'s in-flight entry).
   *
   * This is a secondary guard only: it catches a genuine client rebuild
   * (sign-out / sign-in), NOT an impersonation switch, which leaves the
   * reference intact. The identity segment of the key is what covers that.
   */
  client: unknown;
  overlay: ViewPersonalizationOverlay;
}

/** Caps how many `(contextRef, viewSystemName)` entries the result cache holds — bounded by how many boards one session opens, not unbounded per interaction. */
const MAX_CACHE_ENTRIES = 50;

/**
 * Module-scope RESULT cache — NOT a request coalescer (see
 * `useContextViewPermissions` for that shape, which is deliberately not
 * copied here per `specs/view-personalization-overlay.md`'s Reuse Audit).
 * Entries survive past when their fetch settles, so a remount under the same
 * identity serves the last-known overlay instantly instead of flashing empty
 * while a GET is in flight. `resetViewPersonalizationCache()` drops
 * everything — wired into the org/impersonation-switch reset alongside
 * `useSchemaTypeCacheReset`.
 */
const overlayCache = new Map<string, CachedOverlayEntry>();

/** Drops every cached overlay. Call on identity switch (logout / impersonation change). */
export function resetViewPersonalizationCache(): void {
  overlayCache.clear();
}

function setCacheEntry(key: string, client: unknown, overlay: ViewPersonalizationOverlay): void {
  if (!overlayCache.has(key) && overlayCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = overlayCache.keys().next().value;
    if (oldestKey !== undefined) overlayCache.delete(oldestKey);
  }
  // Delete-then-set (rather than a plain `.set`) bumps the key to the most
  // recently written position, which is what makes the eviction above
  // least-recently-WRITTEN rather than pure insertion order. Reads do not
  // bump, so this is deliberately not a true LRU — writes are what indicate a
  // board is still in use, and a read-only board is the cheapest to re-fetch.
  overlayCache.delete(key);
  overlayCache.set(key, { client, overlay });
}

function overlayFromRecord(record: ContextViewStateOverlay | null): ViewPersonalizationOverlay {
  if (!record) return EMPTY_OVERLAY;
  // The API layer stores `viewConfig` loosely (`Partial<Record<string, unknown>>`)
  // since schema-service treats it as opaque jsonb; here it is always this
  // consumer's own `ViewConfig` shape, same as `SessionLayer.view`.
  return { base: record.baseConfig ?? {}, view: (record.viewConfig ?? {}) as Partial<ViewConfig> };
}

function isOverlayEmpty(overlay: ViewPersonalizationOverlay): boolean {
  return Object.keys(overlay.base).length === 0 && Object.keys(overlay.view).length === 0;
}

/**
 * Reads and debounce-writes the caller's own personalization overlay — the
 * "user" layer of the four-layer resolver (`defaults → view → user → session`).
 *
 * Fails OPEN on read: a network error clears to the empty overlay rather than
 * surfacing an error, since the shared view is always a safe fallback. The
 * previous value is CLEARED, never retained, on both a failing fetch and an
 * identity/key switch — retaining would leak a previous identity's board
 * arrangement across a client swap or a context/view change. This is the
 * deliberately opposite posture from `useContextViewPermissions`, which fails
 * CLOSED because a permission decision must never be optimistic.
 *
 * Writes debounce (`AUTOSAVE_DEBOUNCE_MS`) and coalesce: rapid patches
 * accumulate per-key and flush as ONE PUT carrying the composed patch. The
 * pending buffer composes with `composeJsonPatch`, NOT the `mergeSessionPatch`
 * the local overlay uses — the buffer holds a patch still travelling to the
 * server, where a `null` is an unexecuted delete instruction to retain, while
 * the overlay holds resolved state, where a `null` is a delete to apply.
 * A pending write flushes — never drops — on unmount AND on a context/view
 * key change, so a patch made just before switching boards still lands
 * against the board it was made on, not whichever board is active when the
 * debounce timer fires.
 *
 * See `specs/view-personalization-overlay.md` § UI Components.
 */
export function useViewPersonalization(
  apiClients: ApiClients | null,
  contextRef: string | null | undefined,
  viewSystemName: string | null | undefined,
  options: UseViewPersonalizationOptions = {}
): UseViewPersonalizationReturn {
  const { onWriteError } = options;

  const [overlay, setOverlay] = useState<ViewPersonalizationOverlay>(EMPTY_OVERLAY);
  const [settled, setSettled] = useState(false);

  // `useContext` rather than `useImpersonation()`: the latter throws outside an
  // `ImpersonationProvider`, and design-system components render without one in
  // tests and Storybook. Absent provider === acting as yourself.
  const impersonationSessionId =
    useContext(ImpersonationContext)?.impersonationDetails?.sessionId;
  const actingIdentityKey = impersonationSessionId ?? SELF_IDENTITY_KEY;

  const schemaClientRef = useRef<ApiClient | null>(null);
  const contextRefRef = useRef<string | null | undefined>(null);
  const viewSystemNameRef = useRef<string | null | undefined>(null);
  const actingIdentityRef = useRef<string>(actingIdentityKey);
  const onWriteErrorRef = useRef(onWriteError);
  const pendingBaseRef = useRef<BaseViewConfigPatch | undefined>(undefined);
  const pendingViewRef = useRef<Partial<ViewConfig> | undefined>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Kept fresh every render, independent of the read effect below — a new
  // onWriteError identity must never re-trigger a fetch.
  useEffect(() => {
    onWriteErrorRef.current = onWriteError;
  });

  const flush = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
    const baseConfig = pendingBaseRef.current;
    const viewConfig = pendingViewRef.current;
    if (!baseConfig && !viewConfig) return;

    // Everything that can abort the write is checked BEFORE the buffer is
    // cleared. `apiClients` going transiently null (token expiry, sign-out in
    // flight) nulls these refs in the read effect; clearing first would drop
    // the user's pending arrangement on the floor with no error and no retry.
    const schemaClient = schemaClientRef.current;
    const ref = contextRefRef.current;
    if (!schemaClient || !ref) return;

    pendingBaseRef.current = undefined;
    pendingViewRef.current = undefined;

    const key = buildOverlayCacheKey(ref, viewSystemNameRef.current, actingIdentityRef.current);
    putContextViewState(schemaClient, ref, {
      // Truthiness, not `??`: the body schema declares `minLength: 1`, so an
      // empty string would 400 rather than mean "the default-view row".
      viewSystemName: viewSystemNameRef.current || undefined,
      baseConfig,
      viewConfig,
    })
      .then((result) => {
        setCacheEntry(key, schemaClient, overlayFromRecord(result));
      })
      .catch((err: unknown) => {
        onWriteErrorRef.current?.(err);
      });
  }, []);

  const scheduleFlush = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = undefined;
      flush();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [flush]);

  // ── Read on mount / key change; flush any pending write on the OLD key
  // before switching, in the cleanup — never in a separate effect, since the
  // cleanup closure still sees this render's ref values, before the next
  // render's body overwrites them. ─────────────────────────────────────────
  useEffect(() => {
    const schemaClient = apiClients?.schemaClient ?? null;
    schemaClientRef.current = schemaClient;
    contextRefRef.current = contextRef;
    viewSystemNameRef.current = viewSystemName;
    actingIdentityRef.current = actingIdentityKey;

    if (!schemaClient || !contextRef) {
      setOverlay(EMPTY_OVERLAY);
      // Empty is the RESOLVED answer only when there is nothing to read FOR —
      // a live client and no `contextRef`. A null client is a different state:
      // the read has not happened yet. Settling on it (EAINT-793 R-16) lets a
      // consumer sample `EMPTY_OVERLAY` as truth and latch it — `TreeMapOrganism`
      // reads `settled` as "the read landed" and then permanently latches
      // `hadSuppressedUserLayer: false`, which the later real read can never
      // correct. An event that never fires beats one that reports the opposite
      // of the truth, so stay pending while the client is pending.
      setSettled(schemaClient !== null);
      return () => {
        flush();
      };
    }

    const key = buildOverlayCacheKey(contextRef, viewSystemName, actingIdentityKey);
    const cached = overlayCache.get(key);
    const cacheHit = cached?.client === schemaClient;
    setOverlay(cacheHit ? cached.overlay : EMPTY_OVERLAY);
    // A cache hit is already a real answer — it came from a completed read for
    // this exact key and client. Only a cold read leaves the overlay pending.
    setSettled(cacheHit);

    let cancelled = false;
    getContextViewState(schemaClient, contextRef, viewSystemName ?? undefined)
      .then((record) => {
        if (cancelled) return;
        const resolved = overlayFromRecord(record);
        setOverlay(resolved);
        setCacheEntry(key, schemaClient, resolved);
      })
      .catch(() => {
        if (cancelled) return;
        // Fails OPEN — no overlay is a normal, safe state, not an error.
        // Cleared rather than retained: a stale successful value from before
        // this failure must never keep rendering as if it were current.
        setOverlay(EMPTY_OVERLAY);
        overlayCache.delete(key);
      })
      .finally(() => {
        // Settles on BOTH paths. The catch fails open to "no overlay", which is
        // a resolved answer too — a consumer waiting on `settled` must not hang
        // because the read failed.
        if (!cancelled) setSettled(true);
      });

    return () => {
      cancelled = true;
      flush();
    };
    // `actingIdentityKey` is a dependency so an impersonation switch is handled
    // exactly like a board switch: the cleanup flushes the pending write under
    // the OLD identity's key (the cleanup closure still sees the previous
    // render's refs), then this effect re-reads under the new one.
  }, [apiClients, contextRef, viewSystemName, actingIdentityKey, flush]);

  const patchBase = useCallback(
    (patch: BaseViewConfigPatch) => {
      // A queued delete followed by a re-add of the same key cannot be folded
      // into one merge patch (see `patchFoldsAcrossDelete`) — folding would
      // resurrect sibling keys server-side while the overlay below shows them
      // gone. Send the delete as its own write first. `flush()` also cancels
      // the debounce, so the re-add starts a fresh accumulation window rather
      // than riding the old timer.
      //
      // Two caveats this does NOT cover, both raised for human review in
      // EAINT-789 review pass 3 — the correct fix for each is an ordered write
      // queue, which a single composed buffer cannot express:
      //  - the two PUTs are issued ~800 ms apart, fire-and-forget, with no
      //    sequencing and no failure coupling, so a failed or reordered delete
      //    still lets the re-add merge into the undeleted value; and
      //  - `flush()` returns without clearing the buffer when the client or
      //    context ref is null (token expiry, sign-out in flight), and the
      //    compose below then folds across the delete anyway.
      if (
        patchFoldsAcrossDelete(
          pendingBaseRef.current as Record<string, unknown> | undefined,
          patch as Record<string, unknown>
        )
      ) {
        flush();
      }
      pendingBaseRef.current = composeJsonPatch(
        (pendingBaseRef.current ?? {}) as Record<string, unknown>,
        patch as Record<string, unknown>
      ) as BaseViewConfigPatch;
      setOverlay((prev) => ({
        ...prev,
        base: mergeSessionPatch(
          prev.base as Record<string, unknown>,
          patch as Record<string, unknown>
        ) as Partial<BaseViewConfig>,
      }));
      scheduleFlush();
    },
    [flush, scheduleFlush]
  );

  const patchView = useCallback(
    (patch: Partial<ViewConfig>) => {
      // Same delete-then-re-add hazard as `patchBase` — `viewConfig` carries
      // nested records too (`levelOverrides`), so the fold is reachable here by
      // the same route, and the SAME two caveats apply verbatim: the two PUTs
      // are unsequenced and uncoupled, and a `flush()` that aborts on a null
      // client leaves the buffer in place so the compose below folds across the
      // delete anyway. Both are EAINT-800; see `patchBase` for the detail.
      if (
        patchFoldsAcrossDelete(
          pendingViewRef.current as Record<string, unknown> | undefined,
          patch as Record<string, unknown>
        )
      ) {
        flush();
      }
      pendingViewRef.current = composeJsonPatch(
        (pendingViewRef.current ?? {}) as Record<string, unknown>,
        patch as Record<string, unknown>
      ) as Partial<ViewConfig>;
      setOverlay((prev) => ({
        ...prev,
        view: mergeSessionPatch(
          prev.view as Record<string, unknown>,
          patch as Record<string, unknown>
        ) as Partial<ViewConfig>,
      }));
      scheduleFlush();
    },
    [flush, scheduleFlush]
  );

  const reset = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
    pendingBaseRef.current = undefined;
    pendingViewRef.current = undefined;

    const schemaClient = schemaClientRef.current;
    const ref = contextRefRef.current;
    setOverlay(EMPTY_OVERLAY);

    if (!schemaClient || !ref) return;

    const key = buildOverlayCacheKey(ref, viewSystemNameRef.current, actingIdentityRef.current);
    overlayCache.delete(key);

    try {
      await deleteContextViewState(schemaClient, ref, viewSystemNameRef.current || undefined);
    } catch (err) {
      onWriteErrorRef.current?.(err);
    }
  }, []);

  // Memoized: consumers put this object straight into `useCallback` dependency
  // arrays (TreeMapOrganism's card-view / board-state / reset handlers), so a
  // fresh literal every render would invalidate all three every render and the
  // memoization at those call sites would buy nothing.
  return useMemo(
    () => ({
      overlay,
      hasOverlay: !isOverlayEmpty(overlay),
      patchBase,
      patchView,
      reset,
      settled,
    }),
    [overlay, patchBase, patchView, reset, settled]
  );
}

export default useViewPersonalization;
